import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageInput } from 'stream-chat-react';

import CloseIcon from '@mui/icons-material/Close';
import { DialogTitle, IconButton } from '@mui/material';
import Form from '@common/components/Form';

import { sendBulkMessage } from '@common/api/curators';
import CheckBoxField from '@common/components/Form/CheckBoxField';
import Tabs from '@common/components/Tabs';
import AudienceTab from '@common/modules/Chat/AudienceTab';
import CustomChatInput from '@common/modules/Chat/CustomChatInput';

import { ActionsStyle, ContentStyle, HeaderStyle, ModalStyle, OverlayStyle } from './styled';

interface SimpleModalProps {
	title: string;
	onClose: () => void;
	children: React.ReactNode;
	actions?: React.ReactNode;
}

function SimpleModal({ title, onClose, children, actions }: SimpleModalProps) {
	const modal = (
		<>
			<OverlayStyle role="presentation" onClick={onClose} />
			<ModalStyle role="dialog" aria-modal="true" aria-label={title}>
				<HeaderStyle>
					<DialogTitle sx={{ padding: 0 }} variant="h5">
						{title}
					</DialogTitle>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</HeaderStyle>
				<ContentStyle>{children}</ContentStyle>
				{actions && <ActionsStyle>{actions}</ActionsStyle>}
			</ModalStyle>
		</>
	);

	if (typeof document === 'undefined' || !document.body) {
		return modal;
	}

	return createPortal(modal, document.body);
}

export function buildStreamAttachments({
	imageUploads,
	fileUploads,
}: {
	imageUploads?: Record<string, any>;
	fileUploads?: Record<string, any>;
}) {
	const images = Object.values(imageUploads || {})
		.filter((u) => u.state === 'finished' && u.url)
		.map((u) => ({
			type: 'image',
			image_url: u.url,
			fallback: u.file?.name ?? 'image',
			original_width: u.original_width,
			original_height: u.original_height,
		}));

	const files = Object.values(fileUploads || {})
		.filter((u) => u.state === 'finished' && u.url)
		.map((u) => ({
			type: 'file',
			asset_url: u.url,
			title: u.file?.name,
			mime_type: u.file?.type,
			file_size: u.file?.size,
		}));

	return [...images, ...files];
}

interface MassMailingModalProps {
	onClose: () => void;
}

function MassMailingModal({ onClose }: MassMailingModalProps) {
	const [message, setMessage] = useState('');
	const [attachments, setAttachments] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef<any>(null);

	const handleInputChange = useCallback(
		({ text, attachments }: { text: string; attachments: any[] }) => {
			setMessage(text);
			setAttachments(attachments);
		},
		[],
	);

	const ChatInput = useMemo(
		() =>
			// eslint-disable-next-line react/no-unstable-nested-components
			function ChatInput(props: any) {
				return <CustomChatInput {...props} hideSendButton onChange={handleInputChange} />;
			},
		[handleInputChange],
	);

	const handleSubmit = async (data: any) => {
		setIsLoading(true);
		try {
			await sendBulkMessage({
				...data,
				duplicate_to_email: Boolean(data.duplicate_to_email),
				message,
				attachments,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<SimpleModal title="Mass mailing" onClose={onClose}>
			<Form
				initialValues={{
					program_groups: [],
					duplicate_to_email: false,
				}}
				loading={isLoading}
				onSave={handleSubmit}
				innerRef={formRef}
				actionButtonText="Confirm"
				formActionsStyles={{ display: 'flex', justifyContent: 'flex-end' }}
			>
				<Tabs
					preventUnmount
					header={<CheckBoxField label="Duplicate to email" name="duplicate_to_email" />}
					tabs={[
						{
							label: 'Write a message',
							element: <MessageInput Input={ChatInput} />,
						},
						{
							label: 'Audience',
							element: <AudienceTab />,
						},
					]}
				/>
			</Form>
		</SimpleModal>
	);
}

export default MassMailingModal;
